import BillingViewModel from 'jobs/creation/BillingViewModel'
import { TeamSelection } from 'jobs/creation/DraftChange'
import SubmissionStatusModalViewModel from 'jobs/creation/SubmissionStatusModalViewModel'
import { task } from 'mobx-task'
import { computed, makeObservable } from 'mobx'
import { defer, EMPTY, merge, Observable, of, Subject } from 'rxjs'
import { extractMessageFromError } from 'utils/error-util'
import { every, shareLatest, withCancellation } from 'utils/rx-util'
import { fromStream } from 'utils/mobx-util'
import {
  distinctUntilChanged,
  filter,
  map,
  switchMap,
  first,
  share,
  take,
} from 'rxjs/operators'
import { PublishedLegend } from 'legends/PublishedLegend'
import { CancellationToken } from 'infra/cancellation'
import Member from 'iam/models/Member'
import Job from '../Job'
import { DraftStateSlice } from './DraftStateSlice'
import { StepContainer, StepFactory, StepViewModel } from './OutlineViewModel'
import { AlertPort, BillingPort, SummaryPort } from './ports'
import Summary from '../Summary'
import { QueueChange } from './Persister'
import { Id } from 'types/definitions'

/**
 * Submission View Model.
 */
export default class SubmissionViewModel extends DraftStateSlice {
  /**
   * Used to emit when the request has been submitted.
   * @private
   */
  private readonly onSubmitted = new Subject()

  /**
   * Deferred for the visibility observable.
   *
   * @private
   */
  private readonly isVisible$ = defer(() => this.step.isVisible$)

  /**
   * Emits when we should trigger a summary fetch.
   * The first time the step becomes visible, trigger
   * a fetch, after that we only trigger a fetch when told to refresh.
   * If told to refresh while not visible, we wait until we become visible.
   *
   * @private
   */
  private readonly triggerSummaryFetch$ = this.isVisible$.pipe(
    first(Boolean),
    switchMap(() =>
      merge(
        of(null),
        this.refresh$.pipe(
          switchMap(() => this.isVisible$.pipe(first(Boolean)))
        )
      )
    )
  )

  /**
   * The step VM.
   */
  readonly step: StepViewModel

  /**
   * Changes observable.
   */
  readonly changes$ = EMPTY

  /**
   * Emits when the request has been submitted.
   */
  readonly submitted$ = this.onSubmitted.asObservable()

  /**
   * Whether we have enough data to present the breakdown.
   * Emits the first value only after all the data it depends on
   * is available. This is key to ensuring we don't fetch until then.
   *
   * @private
   */
  private readonly canFetchSummary$ = this.job$.pipe(
    switchMap(() =>
      every([this.allQuestionsAnswered$, this.job$, this.legend$])
    ),
    shareLatest(),
    this.whileActive()
  )

  /**
   * Summary query pipeline.
   *
   * @private
   */
  private readonly summaryQuery$: Observable<Summary | null> =
    this.triggerSummaryFetch$.pipe(
      // We only care about the first emission, since fetches should only happen
      // when the trigger signal emits.
      // NOTE: The reason we use the switch map + first combo here rather than the
      // MobX getter is that this stream won't emit its' first value
      // until it has all the facts, allowing us to defer the initial fetch.
      switchMap(() => this.canFetchSummary$.pipe(first())),
      switchMap((canFetchSummary) =>
        canFetchSummary ? withCancellation(this.fetchSummary) : of(null)
      ),
      share(),
      this.whileActive()
    )

  /**
   * The latest summary.
   *
   * @private
   */
  private readonly summary$ = this.summaryQuery$.pipe(
    shareLatest(),
    this.whileActive()
  )

  /**
   * Emits whenever loading indicators should start.
   *
   * @private
   */
  private readonly loadingStart$ = merge(
    // Whenever we trigger a fetch...
    this.triggerSummaryFetch$,
    // OR whenever we start persisting...
    this.persisting$.pipe(filter(Boolean))
  )

  /**
   * Whether we are currently loading the summary.
   *
   * @private
   */
  private readonly summaryLoading$ = this.loadingStart$.pipe(
    loadingUntil(this.summaryQuery$),
    shareLatest(),
    this.whileActive()
  )

  /**
   * Billing.
   */
  readonly billing = new BillingViewModel(
    this.isActive$,
    this.member$,
    this.team$,
    this.job$,
    this.canFetchSummary$,
    this.queueChange,
    this.adapter,
    this._isDisabled,
    this._canEditDraftJobsAsAdmin
  )

  /**
   * Submission status.
   */
  readonly submissionStatus = new SubmissionStatusModalViewModel(
    this.job$,
    () => this.billing.selectedPaymentMethod,
    this._copyJob,
    () => this.billing.canBypassBilling
  )

  /**
   * Streams as observable.
   *
   * @private
   */
  private readonly $ = {
    canFetchSummary: fromStream(false, this.canFetchSummary$),
    summary: fromStream(null, this.summary$),
    summaryLoading: fromStream(false, this.summaryLoading$),
    legend: fromStream(null, this.legend$),
    job: fromStream(Job.none, this.job$),
  }

  /**
   * Constructor.
   */
  constructor(
    active$: Observable<boolean>,
    createStep: StepFactory,
    private adapter: SummaryPort & BillingPort & AlertPort,
    private queueChange: QueueChange,
    private member$: Observable<Member | null>,
    private team$: Observable<TeamSelection | null>,
    private job$: Observable<Job>,
    private legend$: Observable<PublishedLegend | null>,
    private allQuestionsAnswered$: Observable<boolean>,
    private refresh$: Observable<unknown>,
    private persisting$: Observable<boolean>,
    private _submit: () => Promise<void>,
    private _isValid: () => boolean,
    private _isDisabled: () => boolean,
    private _copyJob: () => Promise<{ jobId: Id }>,
    private _canEditDraftJobsAsAdmin: () => boolean
  ) {
    super(active$)
    makeObservable(this)
    // Reset the task state when we can no longer fetch the summary.
    // This ensures that the UI will render the next loader immediately.
    this.canFetchSummary$
      .pipe(filter((v) => !v))
      .subscribe(() => this.fetchSummary.reset())

    this.step = createStep({
      stepId: 'submission',
      isValid: () => this.billing.isValid,
      outlineItemDisplay: () => {
        return {
          title: 'Order Summary',
          description: 'Please confirm that everything looks correct.',
          value: 'Review your order',
        }
      },
    })
  }

  /**
   * Whether we are currently loading the summary.
   */
  @computed
  get isLoading() {
    return this.$.summaryLoading() || this.billing.isLoading
  }

  /**
   * Whether the current user is allowed to submit the request.
   */
  @computed
  get isAllowedToSubmit() {
    return this.$.job().canSubmit
  }

  /**
   * Whether we are able to submit the request.
   */
  @computed
  get canSubmit() {
    return (
      this.isAllowedToSubmit &&
      !this.isLoading &&
      Boolean(this.summary) &&
      this._isValid()
    )
  }

  /**
   * Legend info.
   */
  @computed
  get legendInfo(): LegendInfo | null {
    const legend = this.$.legend()
    if (!legend) {
      return null
    }

    return {
      title: legend.name,
      imageUrl: legend.imageUrl,
    }
  }

  /**
   * Summary.
   */
  @computed
  get summary() {
    return this.$.summary()
  }

  /**
   * Step containers.
   */
  @computed
  get stepContainers(): StepContainer[] {
    return this.$.canFetchSummary() ? [this] : []
  }

  /**
   * Prepares the VM.
   */
  async prepare(): Promise<void> {
    // Nothing to do here, we are not preventing any rendering.
    // Summary loads happen asynchronously and reactively.
  }

  /**
   * Fetches the summary.
   */
  fetchSummary = task(async (ct: CancellationToken) => {
    const job = this.$.job() || Job.none
    const legend = this.$.legend() || PublishedLegend.none
    const summary = await this.adapter.fetchSummary(job, legend, ct)
    await this.billing.fetchCouponInfo(summary.couponIds, ct)
    return summary
  })

  /**
   * Submit the request.
   */
  submit = task.resolved(async () => {
    this.submissionStatus.startSubmission()
    try {
      await this._submit()
      this.submissionStatus.submissionSuccessful()
      this.onSubmitted.next(undefined)
    } catch (err) {
      this.submissionStatus.submissionFailed(extractMessageFromError(err))
      console.error(err)
    }
  })
}

/**
 * Legend info
 */
export interface LegendInfo {
  imageUrl: string | null
  title: string
}

/**
 * Operator used for creating loading observables.
 *
 * @param endSignal$ Used to signal that a load was completed.
 *  NOTE: This observable should use cancellation!
 */
function loadingUntil(endSignal$: Observable<unknown>) {
  return (startSignal$: Observable<unknown>) =>
    startSignal$.pipe(
      switchMap(() =>
        merge(
          // Emit that we are now loading
          of(true),
          // Once the next query finishes, emit that we are no longer loading
          endSignal$.pipe(
            take(1),
            map(() => false)
          )
        )
      ),
      distinctUntilChanged()
    )
}
